type SpinnerSize = "sm" | "md" | "lg";
type SpinnerVariant = "brand" | "onPrimary";
type SkeletonTone = "subtle" | "emphasis";

const SPINNER_SIZE_CLASS: Record<SpinnerSize, string> = {
  sm: "h-4 w-4",
  md: "h-5 w-5",
  lg: "h-8 w-8",
};

const SPINNER_VARIANT_CLASS: Record<SpinnerVariant, string> = {
  brand: "border-2 border-rag-green/30 border-t-rag-green",
  onPrimary: "border-2 border-white/40 border-t-white",
};

const SKELETON_TONE_CLASS: Record<SkeletonTone, string> = {
  subtle: "bg-guardian-10",
  emphasis: "bg-guardian-20",
};

type SpinnerProps = {
  size?: SpinnerSize;
  variant?: SpinnerVariant;
  className?: string;
};

/** Branded loading spinner for pages, panels, and buttons. */
export function Spinner({
  size = "md",
  variant = "brand",
  className = "",
}: SpinnerProps) {
  return (
    <span
      className={`inline-block shrink-0 animate-spin rounded-full ${SPINNER_SIZE_CLASS[size]} ${SPINNER_VARIANT_CLASS[variant]} ${className}`.trim()}
      aria-hidden
    />
  );
}

type PageLoadingProps = {
  message: string;
  fullScreen?: boolean;
  size?: SpinnerSize;
  messageClassName?: string;
  className?: string;
};

/** Centered page or panel loading state with spinner and message. */
export function PageLoading({
  message,
  fullScreen = false,
  size = "lg",
  messageClassName = "text-sm text-guardian-header",
  className = "",
}: PageLoadingProps) {
  const layoutClass = fullScreen
    ? "min-h-screen flex items-center justify-center"
    : "flex items-center justify-center py-12";

  return (
    <div className={`${layoutClass} ${className}`.trim()}>
      <div className="text-center">
        <Spinner size={size} className="mx-auto mb-4" />
        <p className={messageClassName}>{message}</p>
      </div>
    </div>
  );
}

type InlineLoadingProps = {
  message: string;
  className?: string;
};

/** Inline loading row for tabs, fields, and compact panels. */
export function InlineLoading({ message, className = "" }: InlineLoadingProps) {
  return (
    <div
      className={`inline-flex items-center gap-2 text-sm text-guardian-header ${className}`.trim()}
    >
      <Spinner size="sm" />
      <span>{message}</span>
    </div>
  );
}

type SkeletonBarProps = {
  className?: string;
  tone?: SkeletonTone;
};

/** Pulse placeholder bar matching guardian skeleton styling. */
export function SkeletonBar({
  className = "",
  tone = "emphasis",
}: SkeletonBarProps) {
  return (
    <div
      className={`rounded ${SKELETON_TONE_CLASS[tone]} ${className}`.trim()}
      aria-hidden
    />
  );
}

/** Three-row skeleton for questionnaire history list loading. */
export function HistoryListSkeleton() {
  return (
    <div className="divide-y divide-guardian-10 animate-pulse">
      {[0, 1, 2].map((idx) => (
        <div
          key={idx}
          className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4"
        >
          <div className="min-w-0 space-y-2">
            <SkeletonBar className="h-4 w-36" tone="emphasis" />
            <SkeletonBar className="h-3 w-24" tone="subtle" />
          </div>
          <SkeletonBar className="h-3 w-16 shrink-0" tone="subtle" />
        </div>
      ))}
    </div>
  );
}
